import {
  Dialog,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import * as cadviewer from "cadviewer";
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAlert } from "react-alert";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { CadviewerContext } from "../../context/Cadviewer.Context";
import useConfig from "../../demos/hooks/useConfig";
import EventSystem from "../../events/EventSystem";
import NewFolderForm from "../newFolderForm/UploadFileForm.Component";
import {
  isTabletOrMobile,
  useWindowSize,
} from "../RightAction/RightAction.component";
import TreeView, { ITreeViewOnExpandProps } from "../TreeView";
import { flattenTree } from "../TreeView/utils";
import UploadFileForm from "../UploadFileForm/UploadFileForm.Component";
import { VersionHistoryModal } from "./VersionHistoryModal";
import { BatchProcessModal } from "./BatchProcessModal";
import { JsonEditorModal } from "../JsonEditorModal/JsonEditorModal";
import { SaveAsSvgModal } from "../SaveAsSvgModal/SaveAsSvgModal";
import { useSpaceToolsStore } from "../SpaceTools/stores/spaceToolsStore";
import {
  bmpIcon,
  csvIcon,
  dgnIcon,
  dwfIcon,
  dwgIcon,
  dxfIcon,
  gifIcon,
  jpgIcon,
  jsonIcon,
  pcfIcon,
  pdfIcon,
  pngIcon,
  svgIcon,
  tiffIcon,
  tifIcon,
  unkownIcon,
} from "./icons";

interface FileFolder {
  own?: boolean;
  name: string;
  path: string;
  size: number;
  created: string;
  modified: string;
  isFolder?: boolean;
  children?: FileFolder[];
}

interface FileFolderList extends Array<FileFolder> {}

interface FolderListRightPanelProps {
  rightFolderPanelOpen: boolean;
  setRightFolderPanelOpen: any;
}

const FolderListRightPanel = ({
  rightFolderPanelOpen,
  setRightFolderPanelOpen,
}: FolderListRightPanelProps) => {
  const { config } = useConfig();
  let { ServerBackEndUrl } = config;

  const [folderStructure, setFolderStructure] = useState<FileFolderList>([]);
  const [error, setError] = useState<string | null>(null);
  const { user, setUser } = useContext(CadviewerContext);
  const [openForCompare, setOpenForCompare] = useState(false);
  const [saveAsSvgModalOpen, setSaveAsSvgModalOpen] = useState(false);

  const [serverPaths, setServerPaths] = useState<{ServerLocation: string, folderLocation: string} | null>(null);

  useEffect(() => {
    fetch(`${ServerBackEndUrl}/settings/server-paths`)
      .then(res => res.json())
      .then(data => setServerPaths(data))
      .catch(err => console.error("Failed to fetch server paths", err));
  }, [ServerBackEndUrl]);

  // useEffect(() => {
  // if (!rightFolderPanelOpen){
  // 	setRightFolderPanelOpen(true);
  // }
  // }, [rightFolderPanelOpen, setRightFolderPanelOpen]);

  const fetchFolderStructure = useCallback(async () => {
    try {
      var endpoint = "listdwgdirectory";
      if (ServerBackEndUrl.substring(ServerBackEndUrl.length - 1) == "/") {
        // do nothing
      } else {
        endpoint = "/" + endpoint;
      }

      const headers = new Headers();
      if (user?.token && config.enableLoginFeature) {
        headers.append("Authorization", `Bearer ${user.token}`);
      }

      // fix cors
      headers.append("Content-Type", "application/json");
      headers.append("Accept", "application/json");
      // make the request
      const response = await fetch(
        ServerBackEndUrl +
          endpoint +
          `?loginFeatureEnabled=${config.enableLoginFeature}`,
        {
          method: "GET",
          headers: headers,
        }
      );
      if (!response.ok) {
        if (response.status === 401 || response.status === 403 || response.status === 400) {
          setUser(null);
          setRightFolderPanelOpen(false);
          return;
        }
        //				throw new Error("Network response was not ok");
        console.log("Network response issue: " + response.status + " " + response.statusText);
        throw new Error(`Network response issue: Status ${response.status} (${response.statusText})`);
      }
      const data = await response.json();
      setFolderStructure(data);
      setError(null);
    } catch (error: any) {
      setError(error.message);
    }
  }, [ServerBackEndUrl, user?.token, config.enableLoginFeature, setUser, setRightFolderPanelOpen]);

  const openFolderLocationChooser = (
    data: object | string | number | boolean
  ) => {
    setOpenForCompare(true);
    setRightFolderPanelOpen(true);
  };

  useEffect(() => {
    const handleRefresh = () => {
      fetchFolderStructure();
    };
    const handleSaveSvgOpen = () => {
      setSaveAsSvgModalOpen(true);
    };

    EventSystem.subscribe("panel.folder_list.open", openFolderLocationChooser);
    EventSystem.subscribe("panel.folder_list.refresh", handleRefresh);
    EventSystem.subscribe("modal.save_svg.open", handleSaveSvgOpen);

    return () => {
      EventSystem.unsubscribe("panel.folder_list.open", openFolderLocationChooser);
      EventSystem.unsubscribe("panel.folder_list.refresh", handleRefresh);
      EventSystem.unsubscribe("modal.save_svg.open", handleSaveSvgOpen);
    };
  }, [fetchFolderStructure]);

  useEffect(() => {
    if (!rightFolderPanelOpen) setOpenForCompare(false);
  }, [rightFolderPanelOpen]);

  useEffect(() => {
    fetchFolderStructure();
  }, [fetchFolderStructure, rightFolderPanelOpen]);

  const { width: windowWidth } = useWindowSize();
  const isMobileOrTablet = useMemo(
    () => isTabletOrMobile(windowWidth),
    [windowWidth]
  );

  const renderPanelContent = () => {
    return (
      <Panel
        order={config.actionPanelSide === "left" ? 1 : 2}
        defaultSize={config.rightPanelDefaultWidth}
        className="relative"
      >
        <div className="absolute right-2 top-0 -ml-8 flex pr-2 pt-4 sm:-ml-10 sm:pr-4">
          <button
            type="button"
            className="relative rounded-md text-white hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
            onClick={() => setRightFolderPanelOpen(false)}
          >
            <span className="absolute -inset-2.5" />
            <span className="sr-only">Close panel</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
          <div className="bg-primary-700 px-4 py-6 sm:px-6">
            <div className="flex items-center justify-between">
              <Dialog.Title className="text-base font-semibold leading-6 text-white">
                {config.rightFileModalPanelTitle}
              </Dialog.Title>
            </div>
            <div className="mt-1">
              <p className="text-sm text-primary-300">
                {config.rightFileModalPanelSubTitle}
              </p>
            </div>
            {user && config.enableLoginFeature && (
              <UploadFileForm onFinished={() => fetchFolderStructure()} folderStructure={folderStructure} />
            )}
          </div>
          <div className="relative flex-1 px-4 py-6 sm:px-6">
            {error && <p>Error: {error}</p>}
            <FolderList
              folders={folderStructure}
              fetchFolderStructure={fetchFolderStructure}
              closePanel={() => setRightFolderPanelOpen(false)}
              openForCompare={openForCompare}
              rightFolderPanelOpen={rightFolderPanelOpen}
              serverPaths={serverPaths}
            />
          </div>
        </div>
      </Panel>
    );
  };

  const renderOpacityContent = () => {
    return (
      <Panel
        order={config.actionPanelSide === "left" ? 2 : 1}
        className="opacity-40 bg-black"
        maxSize={isMobileOrTablet ? 20 : undefined}
      ></Panel>
    );
  };

  const filteredFolderStructure = useMemo(() => {
    if (!config.showOnlySubFolder || !config.serverSubFolder) {
      return folderStructure;
    }

    const normalizedServerSubFolder = config.serverSubFolder.endsWith("/")
      ? config.serverSubFolder.slice(0, -1)
      : config.serverSubFolder;

    const targetFolderName = config.serverSubFolder
      .split("/")
      .filter(Boolean)
      .pop();

    const normPath = (p: string) => p.replace(/\\/g, "/");
    const findByPath = (folderList: FileFolderList): FileFolder | null => {
      for (const folder of folderList) {
        if (
          folder.path &&
          normPath(folder.path).endsWith(normalizedServerSubFolder)
        ) {
          return folder;
        }
        if (folder.children) {
          const found = findByPath(folder.children);
          if (found) return found;
        }
      }
      return null;
    };
    const findByName = (folderList: FileFolderList): FileFolder | null => {
      for (const folder of folderList) {
        if (folder.name === targetFolderName) {
          return folder;
        }
        if (folder.children) {
          const found = findByName(folder.children);
          if (found) return found;
        }
      }
      return null;
    };

    const targetFolder = findByPath(folderStructure) ?? findByName(folderStructure);

    return targetFolder?.children || [];
  }, [folderStructure, config.showOnlySubFolder, config.serverSubFolder]);

  return (
    <div>
      <Transition.Root show={rightFolderPanelOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          style={{ zIndex: 10000000 }}
          onClose={setRightFolderPanelOpen}
        >
          <div
            className="fixed inset-0 overflow-hidden"
            style={{ zIndex: 10000001 }}
          >
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex w-full">
                <Dialog.Panel
                  className="pointer-events-auto relative w-96"
                  style={{ width: "100%", maxWidth: "100%" }}
                >
                  <PanelGroup
                    autoSaveId="plan-json-content-panel"
                    direction="horizontal"
                  >
                    {config.actionPanelSide === "left"
                      ? renderPanelContent()
                      : renderOpacityContent()}
                    {/* @ts-ignore */}
                    <PanelResizeHandle className="w-2 bg-gray-300 relative">
                      <div
                        className="h-8 w-4 bg-gray-300 flex items-center justify-center"
                        style={{
                          zIndex: 1000,
                          position: "absolute",
                          bottom: 0,
                          top: "calc(50% - 16px)",
                          left: config.actionPanelSide === "left" ? "auto" : -8,
                          right:
                            config.actionPanelSide === "left" ? -8 : "auto",
                        }}
                      >
                        <svg
                          width="20px"
                          height="32px"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g id="Interface / Drag_Horizontal">
                            <g id="Vector">
                              <path
                                d="M14 18C14 18.5523 14.4477 19 15 19C15.5523 19 16 18.5523 16 18C16 17.4477 15.5523 17 15 17C14.4477 17 14 17.4477 14 18Z"
                                stroke="#fff"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M8 18C8 18.5523 8.44772 19 9 19C9.55228 19 10 18.5523 10 18C10 17.4477 9.55228 17 9 17C8.44772 17 8 17.4477 8 18Z"
                                stroke="#fff"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M14 12C14 12.5523 14.4477 13 15 13C15.5523 13 16 12.5523 16 12C16 11.4477 15.5523 11 15 11C14.4477 11 14 11.4477 14 12Z"
                                stroke="#fff"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M8 12C8 12.5523 8.44772 13 9 13C9.55228 13 10 12.5523 10 12C10 11.4477 9.55228 11 9 11C8.44772 11 8 11.4477 8 12Z"
                                stroke="#fff"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M14 6C14 6.55228 14.4477 7 15 7C15.5523 7 16 6.55228 16 6C16 5.44772 15.5523 5 15 5C14.4477 5 14 5.44772 14 6Z"
                                stroke="#fff"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M8 6C8 6.55228 8.44772 7 9 7C9.55228 7 10 6.55228 10 6C10 5.44772 9.55228 5 9 5C8.44772 5 8 5.44772 8 6Z"
                                stroke="#fff"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </g>
                          </g>
                        </svg>
                      </div>
                    </PanelResizeHandle>
                    {config.actionPanelSide !== "left"
                      ? renderPanelContent()
                      : renderOpacityContent()}
                  </PanelGroup>
                </Dialog.Panel>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      <SaveAsSvgModal
        isOpen={saveAsSvgModalOpen}
        onClose={() => setSaveAsSvgModalOpen(false)}
        folderStructure={filteredFolderStructure}
      />
    </div>
  );
};

  const FolderList = ({
    folders,
    closePanel,
    openForCompare,
    fetchFolderStructure,
    rightFolderPanelOpen,
    serverPaths,
  }: {
    folders: FileFolderList;
    fetchFolderStructure: () => void;
    openForCompare: boolean;
    closePanel: () => void;
    rightFolderPanelOpen: boolean;
    serverPaths: { ServerLocation: string; folderLocation: string } | null;
  }) => {
  const {
    currentDrawingPath,
    setCurrentDrawingPath,
    currentCanvasInstance,
    expendedIds,
    setExpendedIds,
    currentDrawingVersion,
    setCurrentDrawingVersion,
  } = useContext(CadviewerContext);

  const lodFolder = useSpaceToolsStore((state) => state.lodFolder);
  const setLodFolder = useSpaceToolsStore((state) => state.setLodFolder);

  const { user, setUser } = useContext(CadviewerContext);
  const [openUploadFile, setOpenUploadFile] = useState(false);
  const [openNewFolder, setOpenNewFolder] = useState(false);
  const [path, setPath] = useState<string>("");
  const [versionModalData, setVersionModalData] = useState<{
    isOpen: boolean;
    filePath: string;
    fileName: string;
  }>({ isOpen: false, filePath: "", fileName: "" });
  const [batchProcessModalData, setBatchProcessModalData] = useState<{
    isOpen: boolean;
    filePath: string;
    fileName: string;
    jsonFiles: string[];
  }>({ isOpen: false, filePath: "", fileName: "", jsonFiles: [] });
  const [editingJsonFilename, setEditingJsonFilename] = useState<string>("");
  const [editingJsonData, setEditingJsonData] = useState<string>("");
  const [isJsonEditorOpen, setIsJsonEditorOpen] = useState(false);
  const [editingJsonMode, setEditingJsonMode] = useState<"view" | "edit">("view");
  const [editingJsonPath, setEditingJsonPath] = useState<string>("");
  const { config } = useConfig();
  let { ServerBackEndUrl } = config;
  const alert = useAlert();

  const handleLoadJson = async (fullFilePath: string, filename: string, mode: "view" | "edit") => {
    try {
      const normalizedPath = fullFilePath.replace(/\\/g, "/");
      let endpoint = "settings/folders/versions/file";
      let queryParam = `path=${encodeURIComponent(normalizedPath)}`;
      
      if (!ServerBackEndUrl.endsWith("/")) endpoint = "/" + endpoint;

      const response = await fetch(`${ServerBackEndUrl}${endpoint}?${queryParam}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {})
        }
      });

      if (response.ok) {
        const text = await response.text();
        setEditingJsonData(text);
        setEditingJsonFilename(filename);
        setEditingJsonPath(normalizedPath);
        setEditingJsonMode(mode);
        setIsJsonEditorOpen(true);
      } else {
        alert.error(`Failed to load ${filename}`);
      }
    } catch (e) {
      alert.error(`Error loading ${filename}`);
    }
  };

  const handleSaveJsonToServer = async (saveAsNewVersion: boolean = false, versionName?: string) => {
    try {
      if (saveAsNewVersion) {
        let endpoint = "settings/folders/upload";
        if (!ServerBackEndUrl.endsWith("/")) endpoint = "/" + endpoint;

        const normalizedPath = editingJsonPath.replace(/\\/g, "/");
        let folderPath = normalizedPath.split("/").slice(0, -1).join("/");
        
        const contentIndex = folderPath.indexOf("/content/");
        if (contentIndex !== -1) {
          folderPath = folderPath.substring(contentIndex + "/content/".length);
          if (folderPath.startsWith("versions/")) {
            folderPath = folderPath.substring("versions/".length);
          }
        }

        const formdata = new FormData();
        formdata.append("global", "true");
        formdata.append("path", folderPath);
        formdata.append("targetFilename", editingJsonFilename);
        
        const blob = new Blob([editingJsonData], { type: "application/json" });
        formdata.append("files", blob, editingJsonFilename);
        
        const vData: Record<string, string> = {};
        vData[editingJsonFilename] = versionName || "Edited via JSON Editor";
        formdata.append("versionData", JSON.stringify(vData));

        const headers = new Headers();
        if (user?.token) {
          headers.append("Authorization", `Bearer ${user.token}`);
        }

        const response = await fetch(`${ServerBackEndUrl}${endpoint}`, {
          method: "POST",
          body: formdata,
          headers,
        });

        if (response.ok) {
          alert.success(`Successfully saved new version of ${editingJsonFilename}`);
          setIsJsonEditorOpen(false);
        } else {
          const text = await response.text();
          alert.error(`Failed to save new version: ${text}`);
        }
      } else {
        let endpoint = "settings/folders/versions/file";
        if (!ServerBackEndUrl.endsWith("/")) endpoint = "/" + endpoint;
        
        const payload = {
          filePath: editingJsonPath,
          fileContent: editingJsonData,
          versionName: versionName
        };

        const response = await fetch(`${ServerBackEndUrl}${endpoint}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {})
          },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          alert.success(`Successfully saved ${editingJsonFilename}`);
          setIsJsonEditorOpen(false);
        } else {
          alert.error(`Failed to save ${editingJsonFilename}`);
        }
      }
    } catch (e) {
      alert.error(`Error saving ${editingJsonFilename}`);
    }
  };

  // Ref to track if auto-expansion has been performed for the current serverSubFolder
  const lastExpandedServerSubFolderRef = useRef<string | null>(null);

  // Filter folders based on showOnlySubFolder configuration
  const filteredFolders = useMemo(() => {
    if (!config.showOnlySubFolder || !config.serverSubFolder) {
      return folders;
    }

    // Normalize serverSubFolder for path comparison (remove trailing slash)
    const normalizedServerSubFolder = config.serverSubFolder.endsWith("/")
      ? config.serverSubFolder.slice(0, -1)
      : config.serverSubFolder;

    // Extract the last part of the serverSubFolder for name comparison
    const targetFolderName = config.serverSubFolder
      .split("/")
      .filter(Boolean)
      .pop();

    // Two-pass match: an exact path-suffix match anywhere in the tree wins;
    // the last-segment name is only a fallback when no path matches. A single
    // mixed pass in DFS order would let an earlier same-named folder shadow
    // the folder the path actually designates.
    const normPath = (p: string) => p.replace(/\\/g, "/");
    const findByPath = (folderList: FileFolderList): FileFolder | null => {
      for (const folder of folderList) {
        if (
          folder.path &&
          normPath(folder.path).endsWith(normalizedServerSubFolder)
        ) {
          return folder;
        }
        if (folder.children) {
          const found = findByPath(folder.children);
          if (found) return found;
        }
      }
      return null;
    };
    const findByName = (folderList: FileFolderList): FileFolder | null => {
      for (const folder of folderList) {
        if (folder.name === targetFolderName) {
          return folder;
        }
        if (folder.children) {
          const found = findByName(folder.children);
          if (found) return found;
        }
      }
      return null;
    };

    const targetFolder = findByPath(folders) ?? findByName(folders);

    // If target folder is found, return only its children, otherwise return empty array
    return targetFolder?.children || [];
  }, [folders, config.showOnlySubFolder, config.serverSubFolder]);

  const folder = useMemo(
    () => ({
      name: "",
      children: filteredFolders.map((f) => ({
        own: f.own,
        name: f.name,
        path: f.path,
        modified: f.modified,
        isFolder: f.isFolder,
        children: f.children,
      })),
    }),
    [filteredFolders]
  );

  const data = useMemo(() => flattenTree(folder), [folder]);

  // Dynamically compute jsonFiles for the BatchProcessModal
  const batchProcessJsonFiles = useMemo(() => {
    if (!batchProcessModalData.isOpen || !batchProcessModalData.filePath) return [];
    
    // Find the current node inside `data` using its filePath (metadata.path)
    const targetNode = data.find(n => n.metadata?.path === batchProcessModalData.filePath);
    if (!targetNode) return [];

    // Find its siblings
    const siblings = data.filter(n => n.parent === targetNode.parent && n.name?.toLowerCase().endsWith(".json"));
    return siblings.map(n => n.name as string);
  }, [data, batchProcessModalData.isOpen, batchProcessModalData.filePath]);

  // Auto-expand serverSubFolder if configured (only when showOnlySubFolder is false)
  useEffect(() => {
    // Skip auto-expansion if showOnlySubFolder is true since we're already showing the target folder content
    if (config.showOnlySubFolder) {
      return;
    }

    // Only proceed if serverSubFolder is configured and data is available
    if (config.serverSubFolder && data.length > 0) {
      // If the current serverSubFolder is the same as the one we last attempted to expand, skip.
      if (lastExpandedServerSubFolderRef.current === config.serverSubFolder) {
        return;
      }

      // Normalize serverSubFolder for path comparison (remove trailing slash)
      const normalizedServerSubFolder = config.serverSubFolder.endsWith("/")
        ? config.serverSubFolder.slice(0, -1)
        : config.serverSubFolder;

      // Extract the last part of the serverSubFolder for name comparison (filter Boolean to remove empty strings from split)
      const targetFolderName = (config.serverSubFolder ?? "")
        .split("/")
        .filter(Boolean)
        .pop();

      // Two-pass match (same rule as filteredFolders): the path-suffix match
      // wins; a same-named folder is only a fallback when no path matches.
      const targetFolder =
        data.find(
          (node) =>
            typeof node.metadata?.path === "string" &&
            node.metadata.path
              .replace(/\\/g, "/")
              .endsWith(normalizedServerSubFolder)
        ) ??
        data.find((node) => node.isBranch && node.name === targetFolderName);

      if (targetFolder && targetFolder.id !== 0) {
        const ancestors = [];
        let currentId = targetFolder.id;

        while (currentId !== null && currentId !== undefined) {
          const parentNode = data.find((node) => node.id === currentId);
          const parent = parentNode?.parent;

          if (parent !== null && parent !== undefined) {
            ancestors.push(parent);
            currentId = parent;
          } else {
            break;
          }
        }

        const newExpandedIds = [...expendedIds];
        let changed = false;

        if (!newExpandedIds.includes(targetFolder.id)) {
          newExpandedIds.push(targetFolder.id);
          changed = true;
        } else {
        }

        ancestors.forEach((ancestorId) => {
          if (!newExpandedIds.includes(ancestorId)) {
            newExpandedIds.push(ancestorId);
            changed = true;
          } else {
          }
        });

        if (changed) {
          setExpendedIds(newExpandedIds);
          lastExpandedServerSubFolderRef.current = config.serverSubFolder; // Mark as expanded
        } else {
          lastExpandedServerSubFolderRef.current = config.serverSubFolder; // Mark as processed even if no change needed
        }
      } else if (targetFolder && targetFolder.id === 0) {
        lastExpandedServerSubFolderRef.current = config.serverSubFolder; // Mark as attempted to prevent re-runs for root
      } else {
        lastExpandedServerSubFolderRef.current = config.serverSubFolder; // Mark as attempted even if not found
      }
    } else {
    }
  }, [data, config.serverSubFolder, config.showOnlySubFolder, setExpendedIds]);

  // Reset the ref when the panel closes
  useEffect(() => {
    if (!rightFolderPanelOpen) {
      lastExpandedServerSubFolderRef.current = null;
      console.log(
        "🔄 Resetting lastExpandedServerSubFolderRef as panel is closed."
      );
    }
  }, [rightFolderPanelOpen]); // Depend on the new prop

  const openPathInCadViewer = useCallback(
    (path: string, name: string, modified: string) => {
      const availableExtensions = [
        "pdf",
        "tif",
        "tiff",
        "dxf",
        "dwf",
        "dwl",
        "dwl2",
        "dwg",
        "acad",
        "dgn",
        "png",
        "jpeg",
        "jpg",
        "gif",
        "svg",
      ];
      let extension = path.slice(path.lastIndexOf(".") + 1);
      if (!availableExtensions.includes(extension.toLowerCase()) && name) {
        extension = name.slice(name.lastIndexOf(".") + 1);
      }
      if (availableExtensions.includes(extension.toLowerCase())) {
        // Open file in CadViewer
        let fileUrl = path;
        if (serverPaths) {
          const normPath = path.replace(/\\/g, "/");
          const srvLoc = serverPaths.ServerLocation.replace(/\\/g, "/");
          if (normPath.toLowerCase().startsWith(srvLoc.toLowerCase())) {
            const relativePath = normPath.substring(srvLoc.length);
            // Prefix with ServerBackEndUrl instead of exposing absolute file path
            fileUrl = ServerBackEndUrl + (ServerBackEndUrl.endsWith("/") ? "" : "/") + relativePath;
          }
        }

        console.log("1:  Open file in CadViewer", { path, fileUrl });
        // The engine throws out of these calls when it never finished starting
        // up — a config pointing at unreachable content leaves its language
        // table undefined, and cvjs_LoadDrawing dies on it. Unguarded, that
        // took the whole interface down with it: a blank page, from a click on
        // a file. The drawing still will not open, but everything else stays.
        try {
        if (!openForCompare) {
          console.log(
            "openPathInCadViewer cvjs_setISOtimeStamp",
            modified,
            modified.split("T")[0]
          );
          cadviewer.cvjs_setISOtimeStamp(fileUrl, modified + "_" + Date.now().toString());
          //				cadviewer.cvjs_setISOtimeStamp(name, modified.split("T")[0])
          cadviewer.cvjs_setZoomImageWallpaper(false); // 10.30.2

          cadviewer.cvjs_LoadDrawing(
            currentCanvasInstance || "floorPlan",
            fileUrl
          );
          setCurrentDrawingPath(fileUrl);
        } else {
          cadviewer.cvjs_setCompareDrawings_LoadSecondDrawingDirect(
            currentCanvasInstance || "floorPlan",
            fileUrl
          ); // 8.67.17
          cadviewer.cvjs_conversion_addAXconversionParameter("compare", fileUrl); // 8.67.17

          console.log(
            "openPathInCadViewer cvjs_setISOtimeStampCompare",
            name,
            modified.split("T")[0]
          );
          //                if (compareWithDateStamp)
          //                cadviewer.cvjs_setISOtimeStampCompare(name, modified.split("T")[0])
          cadviewer.cvjs_setZoomImageWallpaper(false); // 10.30.2

          cadviewer.cvjs_setISOtimeStampCompare(name, modified + "_" + Date.now().toString());
          cadviewer.cvjs_compareDrawings_LoadSecondDrawing(
            currentCanvasInstance || "floorPlan"
          );
          cadviewer.cvjs_LoadDrawing(
            currentCanvasInstance || "floorPlan",
            currentDrawingPath ?? ""
          ); // 8.67.17
        }
        } catch (err) {
          console.error("CADViewer could not open the drawing:", err);
          alert.error("This drawing could not be opened. Check the viewer's content settings.");
        }
        closePanel();
      }
      },
      [closePanel, ServerBackEndUrl, alert]
    );

  const deleteFolderOrFile = useCallback(
    async (path: any, isFolder: boolean) => {
      try {
        let cleanPath = typeof path === 'string' ? path.replace(/\\/g, "/") : "";
        const contentIndex = cleanPath.indexOf("/content/");
        if (contentIndex !== -1) {
          cleanPath = cleanPath.substring(contentIndex + "/content/".length);
          if (cleanPath.startsWith("versions/")) {
            cleanPath = cleanPath.substring("versions/".length);
          }
        }

        const headers = new Headers();
        headers.append("Authorization", `Bearer ${user?.token}`);
        headers.append("Content-Type", "application/json");

        const response = await fetch(
          ServerBackEndUrl +
            (ServerBackEndUrl.endsWith("/") ? "" : "/") +
            "upload/remove",
          {
            method: "DELETE",
            body: JSON.stringify({ isFolder, path: cleanPath, file: cleanPath, folder: cleanPath, global: "true" }),
            headers,
          }
        );
        const data = await response.json();
        // check if status is not 200
        console.log({ data, response });
        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            setUser(null);
          }
          alert.error(data.error);
        } else {
          alert.success(
            (isFolder ? "Folder" : "File") + " delete successfully."
          );
        }
      } catch (error: any) {
        console.log({ error });
        alert.error("Something went wrong. Please try again later.");
      }
      fetchFolderStructure();
    },
    [fetchFolderStructure]
  );

  const getRelativeSelectedPath = () => {
    let cleanPath = typeof path === 'string' ? path.replace(/\\/g, "/") : "";
    const contentIndex = cleanPath.indexOf("/content/");
    if (contentIndex !== -1) {
      cleanPath = cleanPath.substring(contentIndex + "/content/".length);
      if (cleanPath.startsWith("versions/")) {
        cleanPath = cleanPath.substring("versions/".length);
      }
    }
    return cleanPath;
  };

  return (
    <div>
      <div className="directory">
        {openUploadFile && (
          <UploadFileForm
            onFinished={() => fetchFolderStructure()}
            hideButton
            defaultOpen={openUploadFile}
            path={getRelativeSelectedPath()}
            onClose={() => setOpenUploadFile(false)}
            folderStructure={folders}
          />
        )}
        {openNewFolder && (
          <NewFolderForm
            onFinished={() => {
              setOpenNewFolder(false);
              fetchFolderStructure();
            }}
            open={openNewFolder}
            path={getRelativeSelectedPath()}
          />
        )}
        <TreeView
          data={data}
          onSelect={(node) => {
            if (
              node.isSelected &&
              !node.isBranch &&
              node.element.metadata?.path
            ) {
              const path = (node.element.metadata.path || "") as string;
              const name = node.element.name ?? "";
              if (name.toLowerCase().endsWith(".json")) {
                handleLoadJson(path, name, "view");
                closePanel();
              } else {
                openPathInCadViewer(
                  path,
                  name,
                  (node.element.metadata?.modified ?? "") as string
                );
                setCurrentDrawingVersion("LATEST_FROM_TREE");
              }
            }
          }}
          aria-label="directory tree"
          expandedIds={expendedIds}
          onExpand={(node: ITreeViewOnExpandProps) => {
            if (node.isExpanded) {
              setExpendedIds([...expendedIds, node.element.id]);
            } else {
              setExpendedIds(
                expendedIds.filter((id) => id !== node.element.id)
              );
            }
          }}
          nodeRenderer={({
            element,
            isBranch,
            isExpanded,
            getNodeProps,
            level,
          }) => (
            <div
              {...getNodeProps()}
              /*onClick={() => {
								if(isBranch){
									console.log(`Folder '${element.name}' clicked`);
								} else {
									console.log(`File '${element.name}' clicked`);
								}
							}}*/ className="flex items-center text-xl cursor-pointer my-1 w-full"
              style={{ paddingLeft: 20 * (level - 1) }}
            >
              {isBranch ? (
                <FolderIcon isOpen={isExpanded} own={element.own === true} />
              ) : (
                <FileIcon filename={element.name} />
              )}
              <span className="w-full text-base">{element.name}</span>
              {isBranch && lodFolder === element.metadata?.path && (
                <div className="flex items-center space-x-3 mr-2">
                  <span className="text-[11px] font-bold text-green-600 bg-green-50 border border-green-200 rounded px-1.5 py-0.5 whitespace-nowrap">
                    LOD
                  </span>
                </div>
              )}
              {user?.role === "admin" && element.name !== "MY FOLDER" && (
                <div className="flex items-center space-x-3">
                  {/* Versions Button extracted out of the 3-dots menu */}
                  {!isBranch && config.enableRightFileModalPanelVersionControl !== false && 
                    (config.rightFileModalPanelVersionDisplay || ["dwg", "svg", "dxf", "json"]).includes(element.name?.split('.').pop()?.toLowerCase() || "") && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setVersionModalData({
                          isOpen: true,
                          filePath: (element.metadata?.path || "") as string,
                          fileName: element.name ?? "",
                        });
                      }}
                      className="text-sm font-medium text-[#F67521] hover:text-[#e06619] transition-colors whitespace-nowrap"
                    >
                      Versions
                    </button>
                  )}
                  <Menu
                    as="div"
                    className="relative inline-block text-left"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <div>
                      <MenuButton
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        className="flex items-center rounded-full bg-gray-100 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-100"
                      >
                        <span className="sr-only">Open options</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="size-6"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"
                          />
                        </svg>
                      </MenuButton>
                    </div>

                    <MenuItems
                      transition
                      className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                    >
                      <div className="py-1">
                        {isBranch && (
                          <MenuItem>
                            <span
                              onClick={() => {
                                if (lodFolder === element.metadata?.path) {
                                  setLodFolder(null);
                                } else {
                                  setLodFolder(element.metadata?.path as string);
                                }
                              }}
                              className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 cursor-pointer"
                            >
                              {lodFolder === element.metadata?.path ? "Remove as LOD folder" : "Set as LOD folder"}
                            </span>
                          </MenuItem>
                        )}
                        {isBranch && (
                          <MenuItem>
                            <span
                              onClick={() => {
                                console.log({ element });
                                setPath(`${element.metadata?.path ?? ""}`);
                                setOpenUploadFile(true);
                              }}
                              className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 cursor-pointer"
                            >
                              Upload file
                            </span>
                          </MenuItem>
                        )}
                        {isBranch && (
                          <MenuItem>
                            <span
                              onClick={() => {
                                setPath(`${element.metadata?.path ?? ""}`);
                                setOpenNewFolder(true);
                              }}
                              className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 cursor-pointer"
                            >
                              New Folder
                            </span>
                          </MenuItem>
                        )}
                        {!isBranch &&
                          (element.name?.toLowerCase().endsWith(".dwg") ||
                            element.name?.toLowerCase().endsWith(".dxf")) && (
                            <MenuItem>
                              <span
                                onClick={() => {
                                  setBatchProcessModalData({
                                    isOpen: true,
                                    filePath: (element.metadata?.path || "") as string,
                                    fileName: element.name ?? "",
                                    jsonFiles: [],
                                  });
                                }}
                                className="block w-full px-4 py-2 text-left text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 cursor-pointer"
                              >
                                Batch process VizQuery
                              </span>
                            </MenuItem>
                          )}

                        <MenuItem>
                          <span
                            onClick={() => {
                              deleteFolderOrFile(
                                element.metadata?.path ?? "",
                                isBranch
                              );
                            }}
                            className="block w-full px-4 py-2 text-left text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900"
                          >
                            Delete
                          </span>
                        </MenuItem>
                      </div>
                    </MenuItems>
                  </Menu>
                </div>
              )}
            </div>
          )}
        />
      </div>
      
      {/* Version History Modal */}
      <VersionHistoryModal
        isOpen={versionModalData.isOpen}
        onClose={() => setVersionModalData({ ...versionModalData, isOpen: false })}
        filePath={versionModalData.filePath}
        fileName={versionModalData.fileName}
        currentDrawingPath={currentDrawingPath ?? undefined}
        currentDrawingVersion={currentDrawingVersion ?? undefined}
        onViewVersion={(path, name) => handleLoadJson(path, name, "view")}
        onEditVersion={(path, name) => handleLoadJson(path, name, "edit")}
        onLoadVersion={async (path, name, modified, versionName) => {
          try {
            let endpoint = "settings/folders/clear-conversion-cache";
            if (!ServerBackEndUrl.endsWith("/")) endpoint = "/" + endpoint;
            
            const headers = new Headers();
            headers.append("Content-Type", "application/json");
            if (user?.token) {
              headers.append("Authorization", `Bearer ${user.token}`);
            }
            
            await fetch(`${ServerBackEndUrl}${endpoint}`, {
              method: "POST",
              headers,
              body: JSON.stringify({ filename: name, filePath: versionModalData.filePath })
            });
          } catch (e) {
            console.error("Error clearing conversion cache:", e);
          }

          openPathInCadViewer(path, name, modified);
          setCurrentDrawingVersion(versionName);
        }}
      />
      
      <BatchProcessModal
        isOpen={batchProcessModalData.isOpen}
        onClose={() => setBatchProcessModalData({ ...batchProcessModalData, isOpen: false })}
        onRefresh={() => fetchFolderStructure()}
        filePath={batchProcessModalData.filePath}
        fileName={batchProcessModalData.fileName}
        jsonFiles={batchProcessJsonFiles}
      />
      <JsonEditorModal
        isOpen={isJsonEditorOpen}
        onClose={() => setIsJsonEditorOpen(false)}
        title={editingJsonFilename}
        configData={editingJsonData}
        setConfigData={setEditingJsonData}
        onSave={handleSaveJsonToServer}
        readOnly={editingJsonMode === "view"}
        allowVersioning={true}
      />
    </div>
  );
};

const FolderIcon = ({ isOpen, own }: { isOpen: boolean; own: boolean }) =>
  isOpen ? (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill={own ? "#FCDC12" : "#e8a87c"}
      className="size-6 mr-1"
    >
      <path d="M19.906 9c.382 0 .749.057 1.094.162V9a3 3 0 0 0-3-3h-3.879a.75.75 0 0 1-.53-.22L11.47 3.66A2.25 2.25 0 0 0 9.879 3H6a3 3 0 0 0-3 3v3.162A3.756 3.756 0 0 1 4.094 9h15.812ZM4.094 10.5a2.25 2.25 0 0 0-2.227 2.568l.857 6A2.25 2.25 0 0 0 4.951 21H19.05a2.25 2.25 0 0 0 2.227-1.932l.857-6a2.25 2.25 0 0 0-2.227-2.568H4.094Z" />
    </svg>
  ) : (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill={own ? "#FCDC12" : "#e8a87c"}
      className="size-6 mr-1"
    >
      <path d="M19.5 21a3 3 0 0 0 3-3v-4.5a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h15ZM1.5 10.146V6a3 3 0 0 1 3-3h5.379a2.25 2.25 0 0 1 1.59.659l2.122 2.121c.14.141.331.22.53.22H19.5a3 3 0 0 1 3 3v1.146A4.483 4.483 0 0 0 19.5 9h-15a4.483 4.483 0 0 0-3 1.146Z" />
    </svg>
  );
const FileIcon = ({ filename }: { filename: string }) => {
  // if (false)
  // 	return dirIcon;
  // else
  if (filename.toLocaleLowerCase().endsWith(".json")) return jsonIcon;
  else if (filename.toLocaleLowerCase().endsWith(".dwg")) return dwgIcon;
  else if (filename.toLocaleLowerCase().endsWith(".dxf")) return dxfIcon;
  else if (filename.toLocaleLowerCase().endsWith(".dwf")) return dwfIcon;
  else if (filename.toLocaleLowerCase().endsWith(".dgn")) return dgnIcon;
  else if (filename.toLocaleLowerCase().endsWith(".tiff")) return tiffIcon;
  else if (filename.toLocaleLowerCase().endsWith(".tif")) return tifIcon;
  else if (filename.toLocaleLowerCase().endsWith(".pdf")) return pdfIcon;
  else if (
    filename.toLocaleLowerCase().endsWith(".svg") ||
    filename.toLocaleLowerCase().endsWith(".svgz")
  )
    return svgIcon;
  else if (filename.toLocaleLowerCase().endsWith(".gif")) return gifIcon;
  else if (
    filename.toLocaleLowerCase().endsWith(".jpg") ||
    filename.toLocaleLowerCase().endsWith(".jpeg")
  )
    return jpgIcon;
  else if (filename.toLocaleLowerCase().endsWith(".png")) return pngIcon;
  else if (filename.toLocaleLowerCase().endsWith(".bmp")) return bmpIcon;
  else if (
    filename.toLocaleLowerCase().endsWith(".xls") ||
    filename.toLocaleLowerCase().endsWith(".xlsx") ||
    filename.toLocaleLowerCase().endsWith(".csv")
  )
    return csvIcon;
  else if (filename.toLocaleLowerCase().endsWith(".pcf")) return pcfIcon;
  else return unkownIcon;
};
export default FolderListRightPanel;
